import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Shield, Zap, MapPin, Clock, ChevronRight, AlertTriangle, CheckCircle, Star, ArrowRight } from "lucide-react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { INDUSTRY_TYPES } from "@/constants/industries";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/e6a17b6d-85b8-4581-ad6d-24de2a75247c/images/26b7605a16e6d0f9c77939c038004e540ebcfd08c5c5c9efc108cdce90a4d247.png";
const TAX_PRO = "https://static.prod-images.emergentagent.com/jobs/e6a17b6d-85b8-4581-ad6d-24de2a75247c/images/905d630f165563e2ea24f07f455af8e70b6c9710b4ad26fb539861fba2231666.png";
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const FIPS_TO_STATE = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT",
  "10":"DE","11":"DC","12":"FL","13":"GA","15":"HI","16":"ID","17":"IL",
  "18":"IN","19":"IA","20":"KS","21":"KY","22":"LA","23":"ME","24":"MD",
  "25":"MA","26":"MI","27":"MN","28":"MS","29":"MO","30":"MT","31":"NE",
  "32":"NV","33":"NH","34":"NJ","35":"NM","36":"NY","37":"NC","38":"ND",
  "39":"OH","40":"OK","41":"OR","42":"PA","44":"RI","45":"SC","46":"SD",
  "47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA","54":"WV",
  "55":"WI","56":"WY"
};

const STATE_NAMES = {
  "AL":"Alabama","AK":"Alaska","AZ":"Arizona","AR":"Arkansas","CA":"California",
  "CO":"Colorado","CT":"Connecticut","DE":"Delaware","DC":"D.C.","FL":"Florida",
  "GA":"Georgia","HI":"Hawaii","ID":"Idaho","IL":"Illinois","IN":"Indiana",
  "IA":"Iowa","KS":"Kansas","KY":"Kentucky","LA":"Louisiana","ME":"Maine",
  "MD":"Maryland","MA":"Massachusetts","MI":"Michigan","MN":"Minnesota",
  "MS":"Mississippi","MO":"Missouri","MT":"Montana","NE":"Nebraska","NV":"Nevada",
  "NH":"New Hampshire","NJ":"New Jersey","NM":"New Mexico","NY":"New York",
  "NC":"North Carolina","ND":"North Dakota","OH":"Ohio","OK":"Oklahoma",
  "OR":"Oregon","PA":"Pennsylvania","RI":"Rhode Island","SC":"South Carolina",
  "SD":"South Dakota","TN":"Tennessee","TX":"Texas","UT":"Utah","VT":"Vermont",
  "VA":"Virginia","WA":"Washington","WV":"West Virginia","WI":"Wisconsin","WY":"Wyoming"
};

function getStateFill(count, isHovered) {
  if (isHovered) return "#2563eb";
  if (count === 0) return "#1a2d4a";
  if (count <= 3)  return "#1a3a6c";
  if (count <= 10) return "#1d4ed8";
  return "#2ECC71";
}

function UrgencyBanner({ daysLeft }) {
  return (
    <div className="bg-[#FFD700] text-[#0F172A] py-2 px-4 text-center sticky top-0 z-[60]" data-testid="urgency-banner">
      <div className="flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold flex-wrap">
        <AlertTriangle size={14} className="text-[#856404] flex-shrink-0" />
        <span className="text-[#856404] font-black hidden sm:inline">2027 Season Now Open:</span>
        <span className="hidden sm:inline">Claim your exclusive ZIP code territory before your competitors do.</span>
        <span className="sm:hidden font-black text-[#856404]">2027 Season Open — Claim Your ZIP!</span>
        <span className="bg-[#856404] text-white px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap">
          {daysLeft} DAYS LEFT
        </span>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [searchZip, setSearchZip] = useState("");
  const [stats, setStats] = useState({ active_claims: 0, total_providers: 0 });
  const [daysLeft, setDaysLeft] = useState(0);
  const [mapStats, setMapStats] = useState({ states: {} });
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: "" });

  useEffect(() => {
    axios.get(`${API}/stats/public`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/map/stats`).then(r => setMapStats(r.data)).catch(() => {});
    const deadline = new Date("2027-04-15");
    const today = new Date();
    setDaysLeft(Math.max(0, Math.ceil((deadline - today) / (1000 * 60 * 60 * 24))));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchZip.trim()) navigate(`/find-provider?zip=${searchZip.trim()}`);
  };

  const steps = [
    { icon: <Search size={28} />, title: "Enter Your ZIP Code", desc: "Tell us where you are and we'll find verified providers in your area." },
    { icon: <MapPin size={28} />, title: "Choose a Local Provider", desc: "Compare verified tax pros who offer next-day refund advances near you." },
    { icon: <Zap size={28} />, title: "Get Your Refund Tomorrow", desc: "Your verified provider processes your return and advances your refund fast." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Find Local Tax Professionals — Next-Day Refund Advances Near You"
        description="Search your ZIP code to find verified local tax professionals offering next-day refund advances, refund anticipation loans, and more. Free search. All 50 US states. 2027 tax season."
        canonical="/"
        jsonLd={[
          {
            "@type": "WebSite",
            "@id": "https://nextdayrefund.com/#website",
            "url": "https://nextdayrefund.com/",
            "name": "NextDayRefund.com",
            "description": "Find verified local tax professionals who offer next-day refund advances near you.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": { "@type": "EntryPoint", "urlTemplate": "https://nextdayrefund.com/find-provider?zip={zip_code}" },
              "query-input": "required name=zip_code"
            }
          },
          {
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What is a next-day tax refund advance?", "acceptedAnswer": { "@type": "Answer", "text": "A next-day refund advance is a short-term loan offered by a local tax professional based on your expected tax refund. You receive the money the same day or next day your return is filed." } },
              { "@type": "Question", "name": "How do I find a local tax preparer who offers refund advances?", "acceptedAnswer": { "@type": "Answer", "text": "Enter your ZIP code on NextDayRefund.com to find verified local tax professionals in your area who offer refund anticipation loans and next-day refund advances." } },
              { "@type": "Question", "name": "Is NextDayRefund.com free to use for taxpayers?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, searching for a local tax provider on NextDayRefund.com is completely free for taxpayers." } },
              { "@type": "Question", "name": "How can a business get listed on NextDayRefund.com?", "acceptedAnswer": { "@type": "Answer", "text": "Tax professionals and other service providers can claim an exclusive ZIP code for $150/year, giving them exclusive directory rights for the 2027 tax season." } }
            ]
          }
        ]}
      />
      <UrgencyBanner daysLeft={daysLeft} />
      <Navbar />

      {/* Hero */}
      <section
        className="relative bg-[#003366] min-h-[600px] flex items-center"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-[#003366]/85" />
        <div className="relative section-container py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-white text-sm font-medium mb-6" data-testid="hero-badge">
              <Star size={14} className="text-[#FFD700]" />
              Verified Provider Network — 2027 Tax Season Registration Open
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-6">
              Get Your Tax Refund <span className="text-[#2ECC71]">Tomorrow.</span>
            </h1>
            <p className="text-lg text-slate-200 mb-8 max-w-xl leading-relaxed">
              Find a local verified tax office offering next-day refund advances. Available in all 50 US states — search any ZIP code nationwide.
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="zip-search-form">
              <div className="relative flex-1">
                <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  type="text"
                  placeholder="Enter your ZIP code"
                  value={searchZip}
                  onChange={e => setSearchZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  className="w-full h-14 pl-11 pr-4 rounded-lg bg-white text-[#0F172A] text-lg font-medium border-2 border-transparent focus:outline-none focus:border-[#2ECC71]"
                  maxLength={5}
                  data-testid="zip-search-input"
                />
              </div>
              <button
                type="submit"
                data-testid="zip-search-submit"
                className="btn-action h-14 px-8 text-base whitespace-nowrap"
              >
                Find Providers
              </button>
            </form>

            <div className="flex items-center gap-6 mt-6 text-slate-300 text-sm flex-wrap">
              <div className="flex items-center gap-2"><CheckCircle size={16} className="text-[#2ECC71]" /><span>Free to Search</span></div>
              <div className="flex items-center gap-2"><CheckCircle size={16} className="text-[#2ECC71]" /><span>Verified Providers Only</span></div>
              <div className="flex items-center gap-2"><Shield size={16} className="text-[#2ECC71]" /><span>Secure Platform</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#F8FAFC] border-y border-slate-100 py-8" data-testid="stats-section">
        <div className="section-container">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-3xl font-black text-[#003366]" data-testid="stat-providers">
                {stats.total_providers > 0 ? `${stats.total_providers}+` : "—"}
              </div>
              <div className="text-sm text-[#64748B] mt-1">Verified Providers</div>
            </div>
            <div>
              <div className="text-3xl font-black text-[#003366]" data-testid="stat-zips">
                {stats.active_claims > 0 ? `${stats.active_claims}+` : "—"}
              </div>
              <div className="text-sm text-[#64748B] mt-1">ZIP Codes Covered</div>
            </div>
            <div>
              <div className="text-3xl font-black text-[#003366]" data-testid="stat-states">
                {Object.values(mapStats.states || {}).filter(c => c > 0).length > 0
                  ? `${Object.values(mapStats.states || {}).filter(c => c > 0).length} States`
                  : "All 50"}
              </div>
              <div className="text-sm text-[#64748B] mt-1">States with Providers</div>
            </div>
          </div>
        </div>
      </section>

      {/* IRS Alert Section */}
      <section className="bg-[#FFF8E1] border-y border-[#FFD700]/40 py-10" data-testid="irs-alert-section">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="flex-shrink-0 w-12 h-12 bg-[#FFD700] rounded-lg flex items-center justify-center">
              <AlertTriangle size={24} className="text-[#856404]" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#856404] mb-1">2027 Tax Season Preparation — Act Now</div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-2">Secure Your ZIP Code for the 2027 Tax Season</h2>
              <p className="text-[#334155] leading-relaxed max-w-2xl">
                The 2026 tax season just ended and the race for the 2027 season is already underway. IRS processing delays continue to drive taxpayers toward local next-day refund advance providers. <strong>Claim your exclusive ZIP code now</strong> at the early bird rate before competitors do — and ensure you're the only Verified Provider shown to taxpayers in your area for the entire 2027 season.
              </p>
              <Link to="/for-tax-pros" className="inline-flex items-center gap-2 mt-4 text-[#003366] font-semibold hover:text-[#002244]" data-testid="irs-cta-link">
                Secure your 2027 territory now <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16" data-testid="how-it-works-section">
        <div className="section-container">
          <div className="text-center mb-12">
            <div className="text-xs font-bold uppercase tracking-wider text-[#003366] mb-3">Simple Process</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A]">Get Your Refund in 3 Steps</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={i} className="card-base text-center" data-testid={`step-card-${i + 1}`}>
                <div className="w-14 h-14 bg-[#003366] rounded-xl flex items-center justify-center text-white mx-auto mb-4">
                  {s.icon}
                </div>
                <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-2">Step {i + 1}</div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{s.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── US Territory Map Preview ── */}
      <section className="py-16 bg-[#060B14]" data-testid="map-preview-section">
        <div className="section-container">
          <div className="text-center mb-8">
            <div className="text-xs font-bold uppercase tracking-widest text-[#2ECC71] mb-3 flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#2ECC71]" style={{ animation: "livePulse 1.8s ease-in-out infinite" }} />
              Live Territory Map
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">See Who's Claimed Your Territory</h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto">
              Click any state to explore claimed ZIP codes and available spots. {Object.values(mapStats.states || {}).filter(c => c > 0).length > 0 && <span className="text-[#2ECC71] font-semibold">{Object.values(mapStats.states || {}).filter(c => c > 0).length} states active now.</span>}
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border p-6" style={{ background: "#0a1020", borderColor: "rgba(46,204,113,0.15)" }}>
            <div className="max-w-2xl mx-auto">
              <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "auto" }}>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map(geo => {
                      const fips = String(geo.id).padStart(2, "0");
                      const code = FIPS_TO_STATE[fips];
                      const count = mapStats.states?.[code] || 0;
                      const isHovered = hoveredState === code;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={getStateFill(count, isHovered)}
                          stroke="#060B14"
                          strokeWidth={0.7}
                          onClick={() => code && navigate(`/map?state=${code}`)}
                          onMouseEnter={(e) => {
                            setHoveredState(code);
                            setTooltip({
                              visible: true,
                              x: e.clientX,
                              y: e.clientY,
                              content: `${STATE_NAMES[code] || code}: ${count} claimed`
                            });
                          }}
                          onMouseMove={(e) => setTooltip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
                          onMouseLeave={() => {
                            setHoveredState(null);
                            setTooltip(t => ({ ...t, visible: false }));
                          }}
                          style={{
                            default: { outline: "none", cursor: "pointer" },
                            hover:   { outline: "none", cursor: "pointer" },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>
            </div>
            {/* Tooltip */}
            {tooltip.visible && (
              <div className="fixed z-50 text-white text-xs px-3 py-1.5 rounded-lg pointer-events-none"
                style={{
                  left: tooltip.x + 14,
                  top: tooltip.y - 36,
                  background: "rgba(6,11,20,0.97)",
                  border: "1px solid rgba(46,204,113,0.4)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.6)"
                }}>
                {tooltip.content}
              </div>
            )}
            {/* Legend */}
            <div className="flex items-center justify-center gap-5 mt-2 mb-5 flex-wrap">
              {[
                { color: "#1a2d4a", label: "No Claims" },
                { color: "#1a3a6c", label: "1–3 Claims" },
                { color: "#1d4ed8", label: "4–10 Claims" },
                { color: "#2ECC71", label: "11+ Claims" },
              ].map(l => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm" style={{ background: l.color, border: "1px solid rgba(255,255,255,0.15)" }} />
                  <span className="text-slate-400 text-xs">{l.label}</span>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Link to="/map"
                className="inline-flex items-center gap-2 text-white font-bold px-8 py-3 rounded-xl transition-all hover:scale-105"
                style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)", boxShadow: "0 4px 20px rgba(46,204,113,0.35)" }}
                data-testid="explore-map-btn">
                Explore Full Territory Map <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* For Tax Pros CTA */}
      <section className="bg-[#003366] py-16" data-testid="tax-pros-cta-section">        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="flex-1 text-white">
              <div className="text-xs font-bold uppercase tracking-wider text-[#2ECC71] mb-3">For Tax Professionals</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Own Your ZIP Code. Own Your Market.</h2>
              <p className="text-slate-300 leading-relaxed mb-6 max-w-xl">
                Claim exclusive rights to your zip code and become the only <strong className="text-white">Verified Provider</strong> in your area. High-intent taxpayers searching for next-day refund advances will be sent directly to you — no competition, no bidding.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/claim" className="btn-action" data-testid="hero-claim-cta">
                  Claim Your ZIP Code — $150
                </Link>
                <Link to="/for-tax-pros" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#003366]" data-testid="learn-more-pros">
                  Learn More
                </Link>
              </div>
            </div>
            <div className="hidden md:block w-64 flex-shrink-0">
              <img src={TAX_PRO} alt="Tax Professional" className="rounded-xl shadow-2xl w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      {/* Advertiser Marketplace Promo */}
      <section className="py-16 bg-[#F8FAFC] border-t border-slate-100" data-testid="advertiser-promo-section">
        <div className="section-container">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-widest text-[#003366] mb-2">For Local Businesses</div>
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">Claim Your Exclusive ZIP. Own Your Market.</h2>
            <p className="text-[#64748B] mt-2 max-w-2xl mx-auto">
              Not just for tax professionals. Any local business in 12 industries can own an exclusive ZIP code territory. $150 for the entire 2027 season — one advertiser per industry per ZIP.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 max-w-4xl mx-auto mb-10">
            {[
              { label: "Tax Professional", color: "#2ECC71" },
              { label: "Debt Relief", color: "#f43f5e" },
              { label: "Insurance Agent", color: "#38bdf8" },
              { label: "Mortgage Broker", color: "#14b8a6" },
              { label: "Auto Dealer", color: "#F39C12" },
              { label: "Real Estate", color: "#8B5CF6" },
            ].map(ind => (
              <div key={ind.label} className="bg-white border border-slate-200 rounded-xl p-4 text-center shadow-sm">
                <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ backgroundColor: ind.color }} />
                <div className="text-xs font-semibold text-[#0F172A] leading-tight">{ind.label}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/advertise" className="btn-action !px-10 !py-4 !text-base flex items-center gap-2" data-testid="home-advertise-cta">
              See All {INDUSTRY_TYPES.filter(t => t.value !== "all").length} Industries <ArrowRight size={18} />
            </Link>
            <Link to="/claim" className="border-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white px-10 py-4 rounded-xl font-bold text-base transition-all flex items-center gap-2">
              <MapPin size={16} /> Check My ZIP — Free
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#F8FAFC] border-t border-slate-100" data-testid="trust-section">
        <div className="section-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <Shield size={24} />, title: "Verified Providers", desc: "Every provider is vetted" },
              { icon: <Clock size={24} />, title: "Next-Day Advances", desc: "Fast cash on your refund" },
              { icon: <MapPin size={24} />, title: "Local Experts", desc: "In your neighborhood" },
              { icon: <CheckCircle size={24} />, title: "2027 Season Ready", desc: "Built for this tax season" },
            ].map((t, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2 p-4">
                <div className="text-[#003366]">{t.icon}</div>
                <div className="font-semibold text-[#0F172A] text-sm">{t.title}</div>
                <div className="text-[#64748B] text-xs">{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
